import { breadcrumbSlot } from "@redotech/arbiter/breadcrumb";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ComponentType, memo, useContext } from "react";
import { matchPath, Navigate, useLocation } from "react-router-dom";
import { CartDrawer } from "../../../redo-supplies/cart-drawer";
import { RedoSuppliesCartProvider } from "../../../redo-supplies/redo-supplies-cart-context";
import { RedoSuppliesSessionReplayProvider } from "../../../redo-supplies/supplies-session-replay-provider";
import { defaultChildRoute } from "./legacy-redirects";

export const orderDetailBreadcrumb = breadcrumbSlot();
export const configDetailBreadcrumb = breadcrumbSlot();
export const subscriptionDetailBreadcrumb = breadcrumbSlot();

type PageComponent = ComponentType<{
  hidePadding?: (url: string, search: { search: string }) => boolean;
}>;

type SuppliesLandingComponent = ComponentType;

function shouldHideSuppliesPadding(url: string): boolean {
  return (
    !!matchPath("/stores/:storeId/redo-supplies", url) ||
    !!matchPath("/stores/:storeId/redo-supplies/shop", url) ||
    !!matchPath(
      "/stores/:storeId/redo-supplies/product/:productFamilyId",
      url,
    ) ||
    !!matchPath(
      "/stores/:storeId/redo-supplies/shop/checkout/:checkoutId",
      url,
    ) ||
    !!matchPath("/stores/:storeId/redo-supplies/order-history", url) ||
    !!matchPath("/stores/:storeId/redo-supplies/order-history/:orderId", url) ||
    !!matchPath("/stores/:storeId/redo-supplies/recurring-purchases/*", url)
  );
}

const RedoSuppliesRoutesShell = memo(function RedoSuppliesRoutesShell({
  Page,
}: {
  Page: PageComponent;
}) {
  const team = useContext(TeamContext);
  const location = useLocation();
  const suppliesEnabled = !!team?.settings?.supplies?.enabled;
  const landingMatch = matchPath(
    "/stores/:storeId/redo-supplies",
    location.pathname,
  );
  const routeMatch = matchPath(
    "/stores/:storeId/redo-supplies/*",
    location.pathname,
  );
  const isLandingRoute = !!landingMatch;
  const suppliesBaseUrl = `/stores/${
    landingMatch?.params.storeId ?? routeMatch?.params.storeId
  }/redo-supplies`;

  // `team` is undefined until it loads. Treating that as "supplies off" would
  // bounce any deep link on a cold load: the redirect lands on the base route,
  // which by then has the team and forwards enabled teams to /shop.
  if (!team) {
    return <Page hidePadding={shouldHideSuppliesPadding} />;
  }

  if (!suppliesEnabled && !isLandingRoute) {
    return <Navigate replace to={suppliesBaseUrl} />;
  }

  if (!suppliesEnabled) {
    return <Page hidePadding={shouldHideSuppliesPadding} />;
  }

  return (
    <RedoSuppliesCartProvider>
      <Page hidePadding={shouldHideSuppliesPadding} />
      <CartDrawer />
    </RedoSuppliesCartProvider>
  );
});

const RedoSuppliesIndexRoute = memo(function RedoSuppliesIndexRoute({
  SuppliesLandingPage,
}: {
  SuppliesLandingPage: SuppliesLandingComponent;
}) {
  const team = useContext(TeamContext);
  const suppliesEnabled = !!team?.settings?.supplies?.enabled;

  if (suppliesEnabled) {
    return <Navigate replace to="shop" />;
  }

  return <SuppliesLandingPage />;
});

export const redoSuppliesRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Supplies" },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <RedoSuppliesSessionReplayProvider>
            <RedoSuppliesRoutesShell Page={Page} />
          </RedoSuppliesSessionReplayProvider>
        ),
      };
    },
    children: [
      {
        index: true,
        handle: { breadcrumb: "Welcome" },
        async lazy() {
          const { SuppliesLandingPage } =
            await import("../../../redo-supplies/supplies-landing-page");
          return {
            element: (
              <RedoSuppliesIndexRoute
                SuppliesLandingPage={SuppliesLandingPage}
              />
            ),
          };
        },
      },
      {
        path: "shop",
        handle: { breadcrumb: "Shop" },
        async lazy() {
          const { RedoSuppliesShopPage } =
            await import("../../../redo-supplies/shop-page");
          return { Component: RedoSuppliesShopPage };
        },
      },
      {
        path: "product/:productFamilyId",
        handle: { breadcrumb: "Product" },
        async lazy() {
          const { RedoSuppliesProductDetailPage } =
            await import("../../../redo-supplies/product-detail-page");
          return { Component: RedoSuppliesProductDetailPage };
        },
      },
      {
        path: "shop/checkout/:checkoutId",
        handle: { breadcrumb: "Checkout" },
        async lazy() {
          const { RedoSuppliesCheckoutRoute } =
            await import("../../../redo-supplies/checkout-route");
          return { Component: RedoSuppliesCheckoutRoute };
        },
      },
      {
        path: "order-history",
        handle: { breadcrumb: "Order history" },
        children: [
          {
            index: true,
            async lazy() {
              const { RedoSuppliesOrderHistoryPage } =
                await import("../../../redo-supplies/order-history-page");
              return { Component: RedoSuppliesOrderHistoryPage };
            },
          },
          {
            path: ":orderId",
            handle: { breadcrumb: orderDetailBreadcrumb },
            async lazy() {
              const { RedoSuppliesOrderDetailPage } =
                await import("../../../redo-supplies/order-detail-page");
              return {
                element: (
                  <RedoSuppliesOrderDetailPage
                    breadcrumb={orderDetailBreadcrumb}
                  />
                ),
              };
            },
          },
        ],
      },
      {
        path: "recurring-purchases",
        handle: { breadcrumb: "Recurring purchases" },
        children: [
          defaultChildRoute("inventory"),
          {
            path: "inventory",
            handle: { breadcrumb: "Inventory-based" },
            children: [
              {
                index: true,
                async lazy() {
                  const { RedoSuppliesRecurringPurchasesPage } =
                    await import("../../../redo-supplies/recurring-purchases-page");
                  return { Component: RedoSuppliesRecurringPurchasesPage };
                },
              },
              {
                path: ":configId",
                handle: { breadcrumb: configDetailBreadcrumb },
                async lazy() {
                  const { RedoSuppliesInventoryConfigDetailPage } =
                    await import("../../../redo-supplies/inventory-config-detail-page");
                  return {
                    element: (
                      <RedoSuppliesInventoryConfigDetailPage
                        breadcrumb={configDetailBreadcrumb}
                      />
                    ),
                  };
                },
              },
            ],
          },
          {
            path: "time",
            handle: { breadcrumb: "Time-based" },
            children: [
              {
                index: true,
                async lazy() {
                  const { RedoSuppliesTimeBasedPage } =
                    await import("../../../redo-supplies/time-based-page");
                  return { Component: RedoSuppliesTimeBasedPage };
                },
              },
              {
                path: ":subscriptionId",
                handle: { breadcrumb: subscriptionDetailBreadcrumb },
                async lazy() {
                  const { RedoSuppliesSubscriptionDetailPage } =
                    await import("../../../redo-supplies/subscription-detail-page");
                  return {
                    element: (
                      <RedoSuppliesSubscriptionDetailPage
                        breadcrumb={subscriptionDetailBreadcrumb}
                      />
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
